import React, { useState, useEffect, useContext } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Card, CardBody } from '@/shared/components/Card';
import toast from 'react-hot-toast';
import { CgDetailsMore, CgNotes } from 'react-icons/cg';
import { IoMdTime } from 'react-icons/io';
import { MdOutlineLunchDining, MdRestaurantMenu } from 'react-icons/md';
import { FaPeopleLine, FaMapLocationDot } from 'react-icons/fa6';
import { Button } from '@/shared/components/Button';
import { da } from 'date-fns/locale';
import MealCardForDetails from './MealCardForDetails';
import { Mealcontext } from '../../../../context/MealContext';
import LocationCard from './LocationCard';
import ImageGalleryHorizontal from './NewImageGallery';
import ImageGallery from './ImageGallery';
import ProgressBar from '../../Loader';
import { AuthContext } from '../../../../context/AuthContext';

const MealDetails = () => {
  const [mealData, setMealData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [similarMealLoading, setSimilarMealLoading] = useState(true);
  const [similarMeals, setSimilarMeals] = useState([]);
  const [imagesphoto, setImagesphoto] = useState([]);
  const { apiGlobal } = useContext(AuthContext);
  const { 
    resaurantDetailsId,
    refresh, 
    setclick,
    setActiveKey,
    setIsDetailsClick,
  } = useContext(Mealcontext);

  const imgPath = `${apiGlobal}images/meal/attachment/`;

  // Fetch meal data
  useEffect(() => {
    const fetchMeal = async () => {
      if (!resaurantDetailsId) {
        toast.error('Restaurant details ID not found.');
        return;
      }

      setIsLoading(true);

      try {
        const url = `${apiGlobal}meal_data_fetch.php?id=${resaurantDetailsId}`;
        const response = await fetch(url);
        const data = await response.json();
        // console.log(data);
        if (data.status && data.message === 'Success') {
          setMealData(data.data[0]);
        } else {
          toast.error('Failed to fetch meal data.');
        }
      } catch (e) {
        console.error('Error fetching meal data:', e.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeal();
  }, [resaurantDetailsId, refresh]);

  // Fetch similar meals
  useEffect(() => {
    if (!mealData?.restaurant_id) return;

    const fetchSimilarMeals = async () => {
      setSimilarMealLoading(true);
      try {
        const response = await fetch(
          `${apiGlobal}meal_restaurant_fetch.php?
          restaurant_id=${mealData.restaurant_id}&id=${resaurantDetailsId}`,
        );
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        const data = await response.json();
        setSimilarMeals(data?.data || []);
        // console.log(data);
      } catch (e) {
        console.error('Error fetching similar meals:', e.message);
      } finally {
        setSimilarMealLoading(false);
      }
    };

    fetchSimilarMeals();
  }, [mealData, resaurantDetailsId]);

  // Parse attachment images
  useEffect(() => {
    if (mealData?.attachment) {
      try {
        const images = JSON.parse(mealData.attachment);
        setImagesphoto(images);
      } catch (error) {
        console.error('Failed to parse images:', error);
      }
    }
  }, [mealData]);

  const backHandler = () => {
    setActiveKey('1');
    setclick('searchSelect');
    setIsDetailsClick(false);
  };

  const mealMapping = {
    1: 'Breakfast',
    2: 'Snacks',
    3: 'Lunch',
    4: 'Dinner',
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          // justifyContent: 'center', // Center horizontally
          // alignItems: 'center', // Center vertically
          height: '100vh', // Full viewport height
        }}
      >
        <ProgressBar />
      </div>
    );
  }

  return (
    <Container>
      <div style={{ float: 'right' }}>
        <Button variant="primary" onClick={backHandler}>Back</Button>
      </div>

      <Row>
        <Col xs={12} md={12}>
          <Card>
            <CardBody>
              <div className="row">
                <div className="col-12 col-md-4">
                  <h1>{mealData.name}</h1>
                </div>
                <div className="col-12 col-md-4">
                  <div className="price d-flex">
                    <div className="card-body price d-flex align-items-center">
                      <h2 className="card-text me-2" style={{ color: 'red' }}>
                        {mealData.price} <h3 className="d-inline" style={{ fontSize: '0.75em' }}>AED</h3>
                      </h2>
                      <h2 className="card-text me-2" style={{ color: 'blue' }}>
                        {mealData.final_price} <h3 className="d-inline" style={{ fontSize: '0.75em' }}>AED</h3>
                      </h2>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-2">
                <h5><CgDetailsMore /> {mealData.description || 'N/A'}</h5>
              </div>

              <div className="row mt-2">
                <div className="col-12 col-md-4">
                  <p><IoMdTime />: {mealData.meal_offered
                    ?.split(',')
                    .map(mealId => mealMapping[mealId.trim()] || 'Unknown Meal')
                    .join(', ') || 'N/A'}
                  </p>
                </div>
                <div className="col-12 col-md-4">
                  <p><MdOutlineLunchDining />: {mealData.cuisines}</p>
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-12 col-md-4">
                  <p><MdRestaurantMenu />: {mealData.meal_type}</p>
                </div>
                <div className="col-12 col-md-4">
                  <p><FaPeopleLine />: {mealData.capacity}</p>
                </div>
                <div className="col-12 col-md-4">
                  <p><FaMapLocationDot />: {mealData.locations?.length > 0 
                    ? mealData.locations.map(location => location.location_name?.trim()).join(', ') 
                    : 'No locations available'}
                  </p>
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-12">
                  <h5><CgNotes /> Meal Items</h5> 
                  <div
                    className="table-responsive mt-2"
                    style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #dee2e6' }}
                  >
                    <table className="table table-bordered responsive mt-2 ">
                      <thead className="bg-primary text-white">
                        <tr>
                          <th>Item Name</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {JSON.parse(mealData.items || '[]').map((item, index) => (
                        // eslint-disable-next-line
                        <tr key={index}>
                          <td>{item?.name}</td>
                          <td>{item?.description}</td>
                        </tr>
        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <hr />
              <div className="row mt-2">
                {mealData.locations?.length > 0 
                    ? mealData.locations.map(location => (
                      <div>
                        <h3>Map View : {location?.location_name} </h3>
                        <LocationCard latitude={location?.latitude} longitude={location?.longitude} />
                      </div>
                   ))
                    : 'No locations available'}
              </div>
              <hr />
              <div className="row mt-6">
                <h2>Restaurant - {mealData.restaurant_name}</h2>
              </div>

              <div className="row mt-2">
                <p><CgDetailsMore /> {mealData.restaurant_description || 'N/A'}</p>
              </div>

              <div className="mt-2" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
                {imagesphoto.length > 0 ? <ImageGalleryHorizontal parsedimages={imagesphoto} /> 
                : <p>No images available</p>}
              </div>


              <hr />
              {/* eslint-disable-next-line */}
              {similarMealLoading ? (
                <ProgressBar /> // Show loading spinner
) : similarMeals.length > 0 ? (
  <div className="row mt-4">
    <h2>Similar Meals</h2>
    <div className="similarMeals">
      {similarMeals.map(meal => (
        <MealCardForDetails
          key={meal.id}
          id={meal.id}
          mealname={meal.name}
          restaurantid={meal.restaurant_id}
          description={meal.description}
          type={meal.meal_type}
          price={meal.price}
          finalprice={meal.final_price}
          format={meal.meal_format}
          image={meal.photo}
          isVeg={meal.veg_non_veg}
          mealOffereds={meal.meal_offered}
          cuisine={meal.cuisines}
          capacity={meal.capacity}
          resName={meal.restaurant_name}
        />
      ))}
    </div>
  </div>
) : (
  <h6>No Similar Meals Found!</h6> // Display message if no meals are found
)}

            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MealDetails;
